export const supportedLocales = [
  "af",
  "am",
  "ar",
  "as-in",
  "az-latn-az",
  "be-by",
  "bg",
  "bn-in",
  "bn-bd",
  "bs-latn-ba",
  "ca",
  "ca-es-valencia",
  "cs",
  "cy",
  "da",
  "de",
  "el",
  "en-gb",
  "en-us",
  "es",
  "es-mx",
  "et",
  "eu",
  "fa",
  "fi",
  "fil",
  "fr",
  "fr-ca",
  "ga-ie",
  "gd-gb",
  "gl",
  "gu",
  "ha-latn-ng",
  "he",
  "hi",
  "hr",
  "hu",
  "hy",
  "id",
  "is",
  "it",
  "ja",
  "ka",
  "kk",
  "km-kh",
  "kn",
  "ko",
  "kok",
  "ky",
  "lb-lu",
  "lo-la",
  "lt",
  "lv",
  "mi-nz",
  "mk",
  "ml",
  "mn-mn",
  "mr",
  "ms",
  "mt-mt",
  "ne-np",
  "nl",
  "nn-no",
  "no",
  "or",
  "pa-in",
  "pl",
  "prs",
  "pt",
  "pt-pt",
  "quz-pe",
  "ro",
  "ru",
  "sd-arab-pk",
  "si-lk",
  "sk",
  "sl",
  "sq",
  "sr",
  "sr-cyrl",
  "sr-cyrl-ba",
  "sv",
  "sw",
  "ta",
  "te",
  "th",
  "tk-tm",
  "tr",
  "tt",
  "ug-cn",
  "uk",
  "ur",
  "uz",
  "vi",
  "zh-hans",
  "zh-hant",
  "zu-za",
] as const;

export type SupportedLocale = (typeof supportedLocales)[number];

export function isSupportedLocale(locale: string): locale is SupportedLocale {
  return supportedLocales.indexOf(locale as SupportedLocale) >= 0;
}

export function toSupportedLocale(
  locale: string,
  fallback?: SupportedLocale
): SupportedLocale {
  if (locale) {
    const normalized = locale.toLowerCase().replace("_", "-");

    const fallbackLocale = getFallbackLocale(normalized);
    if (fallbackLocale) {
      return fallbackLocale;
    }

    if (isSupportedLocale(normalized)) {
      return normalized;
    }

    const components = normalized.split("-");
    if (components.length > 2) {
      const withoutRegion = components
        .slice(0, components.length - 1)
        .join("-");
      if (isSupportedLocale(withoutRegion)) {
        return withoutRegion;
      }

      const withoutRegionFallback = getFallbackLocale(withoutRegion);
      if (withoutRegionFallback) {
        return withoutRegionFallback;
      }
    }

    /* If resolution by language code and region failed, let's try to resolve only based on the language code. */
    if (components.length > 1) {
      const languageCode = components[0];
      if (isSupportedLocale(languageCode)) {
        return languageCode;
      }
    }

    /* Try to match the first two characters as a language code. */
    const languageCode = normalized.substr(0, 2);
    if (isSupportedLocale(languageCode)) {
      return languageCode;
    }

    const countryCodeFallback = getFallbackLocale(languageCode);
    if (countryCodeFallback) {
      return countryCodeFallback;
    }

    for (const supportedLocale of supportedLocales) {
      if (supportedLocale.indexOf(languageCode) === 0) {
        return supportedLocale;
      }
    }
  }

  /* We give up. Use the provided fallback or throw an error. */
  if (fallback) {
    return fallback;
  } else {
    throw new Error(`${locale} cannot be mapped to a supported locale`);
  }
}

function getFallbackLocale(locale: string): SupportedLocale | undefined {
  /* Different Latin American Spanish country-specific codes and "es-419" as
   * neutral version of Latin American Spanish. Distinguish it from Castilian
   * found in Europe.
   */
  if (locale.indexOf("es-") === 0 && locale !== "es-es") {
    return "es-mx";
  }

  /* Different flavours of en- except US English can fall back to en-GB */
  if (locale.indexOf("en-") === 0 && locale !== "en-us") {
    return "en-gb";
  }

  if (locale.indexOf("ja-") === 0) {
    return "ja";
  }

  switch (locale) {
    case "zh-hk":
      return "zh-hant";
    case "zh-mo":
      return "zh-hant";
    case "zh-rcn":
      return "zh-hans";
    case "zh-rhk":
      return "zh-hant";
    case "zh-rtw":
      return "zh-hant";
    case "zh-sg":
      return "zh-hant";
    case "zh-tw":
      return "zh-hant";
    case "be":
      return "nl";
    case "ga":
      return "ga-ie";
    case "in":
      return "id";
    case "iw":
      return "he";
    case "nb":
      return "no";
    default:
      return undefined;
  }
}
